/*
 * IdenScan — rule-based training availability
 *
 * Pure, dependency-free logic that generates the bookable training moments from
 * simple rules (a 7-day lead time, a 6-month horizon, closed weekdays, blocked
 * dates and blocked single slots) instead of requiring one section block per
 * bookable date. Existing `training_slot` blocks stay supported as OVERRIDES:
 *   - available === false  -> blocks exactly that date + time
 *   - available === true   -> force-opens that date + time (e.g. a closed Monday)
 *
 * UMD shape so the SAME file is used by the browser engine (as a classic script
 * that sets window.__IDENSCAN_AVAILABILITY__) and by the Node test suite (as a
 * default import). It never touches the DOM, the network, Stripe or personal data.
 */
(function (root, factory) {
  'use strict';
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root && typeof root.window !== 'undefined') root.window.__IDENSCAN_AVAILABILITY__ = api;
  else if (typeof window !== 'undefined') window.__IDENSCAN_AVAILABILITY__ = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  var DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
  var TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
  var DEFAULT_TIMES = ['10:00', '14:00'];
  var DEFAULT_DURATION_MINUTES = 90;

  function pad2(value) {
    return String(value).padStart(2, '0');
  }

  function isoDate(date) {
    return date.getFullYear() + '-' + pad2(date.getMonth() + 1) + '-' + pad2(date.getDate());
  }

  function startOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
  }

  function addDays(date, days) {
    var next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
  }

  function addMonths(date, months) {
    var next = new Date(date);
    next.setMonth(next.getMonth() + months);
    return next;
  }

  function endOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
  }

  // Local-time Date for a "YYYY-MM-DD" + "HH:MM" pair (Europe/Amsterdam in prod).
  function parseLocal(dateIso, time) {
    var d = dateIso.split('-').map(Number);
    var t = time.split(':').map(Number);
    return new Date(d[0], d[1] - 1, d[2], t[0], t[1], 0, 0);
  }

  function addMinutesToTime(time, minutes) {
    var parts = time.split(':').map(Number);
    var total = parts[0] * 60 + parts[1] + minutes;
    var wrapped = ((total % (24 * 60)) + 24 * 60) % (24 * 60);
    return pad2(Math.floor(wrapped / 60)) + ':' + pad2(wrapped % 60);
  }

  function toStringList(value) {
    if (Array.isArray(value)) return value.map(function (item) { return String(item).trim(); }).filter(Boolean);
    // Accept newline- or comma-separated raw text from a section setting.
    return String(value || '')
      .split(/[\n,]+/)
      .map(function (item) { return item.trim(); })
      .filter(Boolean);
  }

  function normalizeAvailabilityConfig(config) {
    config = config || {};
    var leadRaw = Number.parseInt(config.leadDays, 10);
    var leadDays = Number.isFinite(leadRaw) && leadRaw >= 0 ? Math.min(365, leadRaw) : 7;

    var horizonRaw = Number.parseInt(config.horizonMonths, 10);
    var horizonMonths = Number.isFinite(horizonRaw) && horizonRaw >= 1 ? Math.min(24, horizonRaw) : 6;

    var closedWeekdays = toStringList(config.closedWeekdays != null ? config.closedWeekdays : config.closedWeekdaysRaw)
      .map(function (n) { return Number.parseInt(n, 10); })
      .filter(function (n) { return Number.isInteger(n) && n >= 0 && n <= 6; });

    var times = (Array.isArray(config.times) ? config.times : DEFAULT_TIMES)
      .map(function (t) { return String(t).trim(); })
      .filter(function (t) { return TIME_RE.test(t); });

    var blockedDates = toStringList(config.blockedDates != null ? config.blockedDates : config.blockedDatesRaw)
      .filter(function (d) { return DATE_RE.test(d); });

    var blockedSlots = toStringList(config.blockedSlots != null ? config.blockedSlots : config.blockedSlotsRaw)
      .map(function (s) { return s.replace(/\s+/g, ' ').trim(); })
      .filter(function (s) {
        var bits = s.split(' ');
        return DATE_RE.test(bits[0]) && TIME_RE.test(bits[1] || '');
      });

    return {
      leadDays: leadDays,
      horizonMonths: horizonMonths,
      // Default: Sunday (0), Monday (1) and Saturday (6) are closed. An empty
      // setting keeps that weekend + Monday default.
      closedWeekdays: closedWeekdays.length ? closedWeekdays : [0, 1, 6],
      times: times.length ? times : DEFAULT_TIMES.slice(),
      blockedDates: blockedDates,
      blockedSlots: blockedSlots,
      durationMinutes: Number.isFinite(config.durationMinutes) ? config.durationMinutes : DEFAULT_DURATION_MINUTES,
      overrides: Array.isArray(config.overrides) ? config.overrides : []
    };
  }

  // The bookable window: [today + leadDays 00:00, today + horizonMonths 23:59].
  function getBookingWindow(config, now) {
    var cfg = normalizeAvailabilityConfig(config);
    var today = startOfDay(now || new Date());
    return {
      first: addDays(today, cfg.leadDays),
      last: endOfDay(addMonths(today, cfg.horizonMonths))
    };
  }

  function makeSlot(dateIso, time, packages, deliveries, available, cfg, endTime) {
    var end = TIME_RE.test(String(endTime || '')) ? endTime : addMinutesToTime(time, cfg.durationMinutes);
    return {
      id: 'slot_' + dateIso + '_' + time.replace(':', ''),
      date: dateIso,
      startTime: time,
      endTime: end,
      startsAt: parseLocal(dateIso, time),
      endsAt: parseLocal(dateIso, end),
      available: available,
      packages: packages.slice(),
      deliveries: deliveries.slice(),
      label: '',
      timezone: 'Europe/Amsterdam'
    };
  }

  function normalizePackages(value) {
    var v = String(value || 'both').toLowerCase();
    // Start, Team and Actieplan all book the SAME moments; the package only
    // changes the reserved duration. A named override targets one package; any
    // other value opens the moment to all three.
    return v === 'start' || v === 'team' || v === 'action' ? [v] : ['start', 'team', 'action'];
  }

  function normalizeDeliveries(value) {
    var v = String(value || 'both').toLowerCase();
    return v === 'online' || v === 'on_location' ? [v] : ['online', 'on_location'];
  }

  // Generate every bookable slot for the window, applying closed weekdays,
  // blocked dates, blocked single slots, and training_slot overrides.
  function buildAvailability(config, now) {
    var cfg = normalizeAvailabilityConfig(config);
    var window = getBookingWindow(cfg, now || new Date());
    var first = window.first;
    var last = window.last;
    var closed = new Set(cfg.closedWeekdays);
    var blockedDates = new Set(cfg.blockedDates);
    var blockedSlots = new Set(cfg.blockedSlots);
    var map = new Map();

    // 1) Rule-based generation across the whole window.
    for (var cursor = new Date(first); cursor <= last; cursor = addDays(cursor, 1)) {
      if (closed.has(cursor.getDay())) continue;
      var iso = isoDate(cursor);
      if (blockedDates.has(iso)) continue;
      for (var i = 0; i < cfg.times.length; i += 1) {
        var time = cfg.times[i];
        if (blockedSlots.has(iso + ' ' + time)) continue;
        map.set(iso + ' ' + time, makeSlot(iso, time, ['start', 'team', 'action'], ['online', 'on_location'], true, cfg));
      }
    }

    // 2) training_slot overrides (exceptions + explicit extra moments).
    for (var j = 0; j < cfg.overrides.length; j += 1) {
      var override = cfg.overrides[j] || {};
      var date = String(override.date || '').trim();
      var otime = String(override.startTime || '').trim();
      if (!DATE_RE.test(date) || !TIME_RE.test(otime)) continue;
      var startsAt = parseLocal(date, otime);
      if (startsAt < first || startsAt > last) continue;
      var key = date + ' ' + otime;
      if (override.available === false) {
        map.delete(key);
      } else {
        map.set(key, makeSlot(
          date,
          otime,
          normalizePackages(override.package || (override.packages && override.packages.join(','))),
          normalizeDeliveries(override.delivery || (override.deliveries && override.deliveries.join(','))),
          true,
          cfg,
          override.endTime
        ));
      }
    }

    return Array.from(map.values()).sort(function (a, b) { return a.startsAt - b.startsAt; });
  }

  return {
    buildAvailability: buildAvailability,
    getBookingWindow: getBookingWindow,
    normalizeAvailabilityConfig: normalizeAvailabilityConfig,
    isoDate: isoDate
  };
});
